/**
 * BulkEmailModal — Sprint 80
 *
 * Modal partagée pour envoyer un email à une sélection d'entités CRM
 * (prospects | contacts | applications) en utilisant un template unifié
 * (table `email_templates`) avec auto-détection de la langue par destinataire.
 *
 * Props:
 *   - open: boolean
 *   - onClose: () => void
 *   - entity: "prospects" | "contacts" | "applications"
 *   - ids: string[]              (ids des entités sélectionnées)
 *   - typeFilter?: string        (filtre par défaut sur le type de template, ex: "prospect")
 *   - onSent?: () => void        (callback après envoi pour rafraîchir la liste)
 *   - allowSegment?: boolean     (Sprint 86 — propose un sélecteur de segment, contacts uniquement)
 *   - initialSegmentTag?: string (Sprint 86 — pré-sélectionne un segment au montage)
 */
import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Loader2, Eye, Languages, Mail, Globe, Filter } from "lucide-react";

const LANG_LABELS = {
  fr: "🇫🇷 Français",
  en: "🇬🇧 English",
  es: "🇪🇸 Español",
  it: "🇮🇹 Italiano",
  pt: "🇵🇹 Português",
  de: "🇩🇪 Deutsch",
};

const ENTITY_LABEL = { prospects: "prospect", contacts: "contact", applications: "candidat" };
const ENTITY_BUTTON_LABEL = { prospects: "prospects", contacts: "contacts", applications: "candidats" };

const interpolate = (s, vars) =>
  (s || "").replace(/\{\{\s*([\w_]+)\s*\}\}/g, (_, k) => vars[k] ?? `{{${k}}}`);

const tagLabel = (tag, groups) => {
  if (!tag) return "";
  for (const g of (groups || [])) {
    const hit = (g.values || []).find((v) => v.value === tag);
    if (hit) return `${g.label} · ${hit.label}`;
  }
  return tag;
};

export default function BulkEmailModal({ open, onClose, entity, ids, typeFilter, onSent, allowSegment = false, initialSegmentTag = "" }) {
  const [templates, setTemplates] = useState([]);
  const [recipientStats, setRecipientStats] = useState(null);
  const [selectedSlug, setSelectedSlug] = useState("");
  const [langMode, setLangMode] = useState("auto");
  const [forcedLang, setForcedLang] = useState("fr");
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");
  const [preview, setPreview] = useState(null);
  const [sending, setSending] = useState(false);

  // Sprint 86 — segment targeting (contacts only)
  const segmentEnabled = allowSegment && entity === "contacts";
  const [segments, setSegments] = useState([]); // [{key,label,values:[{value,label,count}]}]
  const [segmentTag, setSegmentTag] = useState(initialSegmentTag);
  // Re-seed internal state every time the modal opens (or initialSegmentTag changes from parent)
  // because the modal stays mounted while closed (returns null at the bottom).
  useEffect(() => {
    if (open) setSegmentTag(initialSegmentTag || "");
  }, [open, initialSegmentTag]);
  const useSegment = segmentEnabled && !!segmentTag;

  // Load templates + segments + preview recipients on open
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const r = await api.get("/admin/settings/email-templates");
        setTemplates(r.data || []);
      } catch { /* ignore */ }
      if (segmentEnabled) {
        try {
          const r = await api.get("/admin/contacts/segments");
          setSegments(r.data?.groups || []);
        } catch { /* ignore */ }
      }
    })();
  }, [open, segmentEnabled]);

  // Recompute preview whenever ids or segment selection change
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const body = useSegment
          ? { entity, segment_tag: segmentTag }
          : { entity, ids };
        if (!useSegment && (!ids || ids.length === 0)) { setRecipientStats(null); return; }
        const r2 = await api.post("/admin/bulk-email/preview", body);
        setRecipientStats(r2.data);
      } catch {
        setRecipientStats(null);
      }
    })();
  }, [open, entity, ids, useSegment, segmentTag]);

  // Group templates by slug (one row per unique template family)
  const templatesBySlug = useMemo(() => {
    const map = {};
    for (const t of templates) {
      if (!t.slug) continue;
      if (typeFilter && (t.type || "transactional") !== typeFilter && (t.type || "transactional") !== "transactional" && (t.type || "transactional") !== "contact") {
        // Soft filter: prioritize the requested type but still let admins pick from "transactional" or "contact" generic ones.
        continue;
      }
      if (!map[t.slug]) {
        map[t.slug] = { slug: t.slug, type: t.type || "transactional", description: t.description || "", langs: {} };
      }
      map[t.slug].langs[t.lang] = t;
    }
    // Sort: requested type first, then alpha
    return Object.values(map).sort((a, b) => {
      const ta = a.type === typeFilter ? 0 : 1;
      const tb = b.type === typeFilter ? 0 : 1;
      if (ta !== tb) return ta - tb;
      return a.slug.localeCompare(b.slug);
    });
  }, [templates, typeFilter]);

  // When user picks a template, pre-fill subject/body with the FR variant (admin can tweak)
  useEffect(() => {
    if (!selectedSlug) { setSubject(""); setBody(""); return; }
    const fam = templatesBySlug.find((x) => x.slug === selectedSlug);
    if (!fam) return;
    const ref = fam.langs.fr || Object.values(fam.langs)[0];
    if (ref) {
      setSubject(ref.subject || "");
      setBody(ref.body || "");
    }
  }, [selectedSlug, templatesBySlug]);

  const doPreview = () => {
    const sample = recipientStats?.sample?.[0] || { first_name: "Marie", last_name: "Dupré", company_name: "VineaTech", contact_name: "Marie Dupré" };
    const vars = {
      first_name: sample.name?.split(" ")[0] || sample.first_name || "Marie",
      last_name: (sample.name?.split(" ").slice(1).join(" ")) || sample.last_name || "Dupré",
      contact_name: sample.name || "Marie Dupré",
      company_name: sample.company || sample.company_name || "VineaTech",
      email: sample.email || "marie@example.com",
      country: sample.country || "France",
    };
    setPreview({
      subject: interpolate(subject, vars),
      html: `<html><body style="font-family:Inter,system-ui,sans-serif;padding:24px;max-width:680px;margin:auto">${interpolate(body, vars)}</body></html>`,
      vars,
    });
  };

  const send = async () => {
    if (!selectedSlug && !subject.trim()) { toast.error("Choisissez un template ou tapez un sujet"); return; }
    if (!subject.trim() || !body.trim()) { toast.error("Sujet et corps requis"); return; }
    const totalRecipients = useSegment ? (recipientStats?.total || 0) : (ids?.length || 0);
    if (!useSegment && !ids?.length) { toast.error("Aucun destinataire sélectionné"); return; }
    if (useSegment && totalRecipients === 0) { toast.error("Aucun destinataire dans ce segment"); return; }
    const targetLabel = useSegment
      ? `${totalRecipients} contact${totalRecipients > 1 ? "s" : ""} du segment « ${tagLabel(segmentTag, segments)} »`
      : `${ids.length} ${ENTITY_BUTTON_LABEL[entity]}`;
    if (!window.confirm(`Envoyer cet email à ${targetLabel} ?`)) return;
    setSending(true);
    try {
      const payload = {
        entity,
        ...(useSegment ? { segment_tag: segmentTag, ids: [] } : { ids }),
        template_slug: selectedSlug || "_inline_override_",
        lang_mode: langMode,
        forced_lang: forcedLang,
        subject_override: subject,
        body_override: body,
      };
      const r = await api.post("/admin/bulk-email/send", payload);
      toast.success(`📨 Envoi lancé en arrière-plan — ${r.data?.queued || totalRecipients} destinataire${(r.data?.queued || totalRecipients) > 1 ? "s" : ""}`);
      onSent?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur d'envoi");
    } finally {
      setSending(false);
    }
  };

  if (!open) return null;

  const selectedFamily = templatesBySlug.find((x) => x.slug === selectedSlug);
  const availableLangs = selectedFamily ? Object.keys(selectedFamily.langs) : Object.keys(LANG_LABELS);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose}>
      <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-email-modal">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-neutral-100 px-6 py-4">
          <div>
            <h3 className="text-xl font-bold text-navy"><Mail className="mr-1.5 inline h-5 w-5 text-wine-500" /> Envoi groupé d&apos;email</h3>
            <p className="mt-0.5 text-xs text-neutral-500">
              {useSegment ? (
                <>Cible : <strong>segment « {tagLabel(segmentTag, segments) || segmentTag} »</strong>{recipientStats && <> · {recipientStats.total} destinataire{recipientStats.total > 1 ? "s" : ""}</>}</>
              ) : (
                <>{ids.length} {ENTITY_LABEL[entity] || "destinataire"}{ids.length > 1 ? "s" : ""} sélectionné{ids.length > 1 ? "s" : ""}{recipientStats && <span className="ml-2">· {recipientStats.total} avec email valide</span>}</>
              )}
            </p>
          </div>
          <button onClick={onClose} className="grid h-8 w-8 place-items-center rounded-full hover:bg-neutral-100"><X className="h-4 w-4" /></button>
        </div>

        <div className="p-6 space-y-4">
          {/* Sprint 86 — Segment picker (contacts only, when enabled) */}
          {segmentEnabled && (
            <div className="rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
              <label className="text-[10px] font-bold uppercase tracking-wider text-indigo-700"><Filter className="mr-1 inline h-3 w-3" /> Cibler un segment de contacts</label>
              <select
                value={segmentTag}
                onChange={(e) => setSegmentTag(e.target.value)}
                className="mt-1.5 w-full rounded-xl border border-indigo-200 bg-white px-3 py-2 text-sm"
                data-testid="bulk-segment-select"
              >
                <option value="">— Aucun (utiliser la sélection {ids?.length ? `(${ids.length})` : ""}) —</option>
                {segments.map((g) => (
                  <optgroup key={g.key} label={g.label}>
                    {g.values.filter((v) => v.count > 0).map((v) => (
                      <option key={v.value} value={v.value}>{v.label} · {v.count}</option>
                    ))}
                  </optgroup>
                ))}
              </select>
              <p className="mt-1 text-[10px] text-indigo-600">
                Quand un segment est choisi, l&apos;email est envoyé à <strong>tous</strong> les contacts qui portent ce tag (les destinataires sont résolus côté serveur).
              </p>
            </div>
          )}
          {/* Recipient breakdown */}
          {recipientStats && (
            <div className="rounded-xl border border-wine-100 bg-wine-50/40 p-3 text-xs">
              <div className="font-semibold text-wine-800"><Globe className="mr-1 inline h-3.5 w-3.5" /> Langues détectées chez les destinataires</div>
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {Object.entries(recipientStats.by_lang || {}).map(([lang, count]) => (
                  <span key={lang} className="inline-flex items-center gap-1 rounded-full bg-white px-2 py-0.5 font-semibold text-wine-700 border border-wine-200">{LANG_LABELS[lang] || lang} · {count}</span>
                ))}
                {Object.keys(recipientStats.by_lang || {}).length === 0 && <span className="text-neutral-500">Aucune</span>}
              </div>
            </div>
          )}

          {/* Template selector */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Template (depuis Emailings → Templates)</label>
            <select value={selectedSlug} onChange={(e) => setSelectedSlug(e.target.value)} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="bulk-template-select">
              <option value="">— Email libre (pas de template) —</option>
              {templatesBySlug.map((fam) => (
                <option key={fam.slug} value={fam.slug}>
                  {fam.slug} ({Object.keys(fam.langs).join("/").toUpperCase()})
                  {fam.description ? ` — ${fam.description.slice(0, 60)}` : ""}
                </option>
              ))}
            </select>
            {selectedFamily && (
              <p className="mt-1 text-[11px] text-neutral-500">
                Langues disponibles pour ce template : {availableLangs.map((l) => LANG_LABELS[l] || l).join(" · ")}
              </p>
            )}
          </div>

          {/* Lang mode */}
          <div className="rounded-xl border border-neutral-200 bg-neutral-50/50 p-3">
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500"><Languages className="mr-1 inline h-3 w-3" /> Langue</label>
            <div className="mt-2 flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" name="lang-mode" checked={langMode === "auto"} onChange={() => setLangMode("auto")} data-testid="bulk-lang-auto" />
                <span><strong>Auto</strong> — chaque destinataire reçoit l&apos;email dans sa langue (pays/profil → fallback FR)</span>
              </label>
            </div>
            <div className="mt-1.5 flex flex-wrap items-center gap-2">
              <label className="inline-flex items-center gap-1.5 text-sm">
                <input type="radio" name="lang-mode" checked={langMode === "forced"} onChange={() => setLangMode("forced")} data-testid="bulk-lang-forced" />
                <span>Forcer&nbsp;:</span>
              </label>
              <select disabled={langMode !== "forced"} value={forcedLang} onChange={(e) => setForcedLang(e.target.value)} className="rounded-full border border-neutral-200 bg-white px-3 py-1 text-xs disabled:opacity-50" data-testid="bulk-forced-lang">
                {Object.entries(LANG_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>

          {/* Subject + body (editable, prefilled from template) */}
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Sujet *</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Sujet (peut contenir {{first_name}})" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="bulk-subject" />
          </div>
          <div>
            <label className="text-[10px] font-bold uppercase tracking-wider text-neutral-500">Corps HTML *</label>
            <textarea rows={9} value={body} onChange={(e) => setBody(e.target.value)} placeholder="<p>Bonjour {{first_name}},</p>" className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm font-mono" data-testid="bulk-body" />
            <p className="mt-1 text-[10px] text-neutral-400">
              Si vous tweakez ces champs, ils écrasent le template choisi (l&apos;envoi utilise le contenu visible ci-dessus, interpolé par destinataire). En mode <strong>Auto</strong>, le template change automatiquement de langue mais vos overrides sont conservés.
            </p>
          </div>

          {/* Actions */}
          <div className="flex flex-wrap justify-end gap-2 border-t border-neutral-100 pt-4">
            <button onClick={doPreview} disabled={!subject || !body} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-300 bg-white px-4 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50 disabled:opacity-40" data-testid="bulk-preview-btn">
              <Eye className="h-3.5 w-3.5" /> Aperçu
            </button>
            <button onClick={onClose} className="rounded-full border border-neutral-300 px-4 py-2 text-xs font-semibold text-neutral-700">Annuler</button>
            <button onClick={send} disabled={sending || !subject || !body || (useSegment ? !recipientStats?.total : !ids.length)} className="inline-flex items-center gap-1.5 rounded-full bg-wine-600 px-5 py-2 text-xs font-bold text-white hover:bg-wine-700 disabled:opacity-40" data-testid="bulk-send-btn">
              {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
              Envoyer à {useSegment ? (recipientStats?.total || 0) : ids.length}
            </button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="fixed inset-0 z-[60] grid place-items-center bg-black/70 p-4" onClick={() => setPreview(null)}>
          <div className="w-full max-w-3xl rounded-2xl bg-white p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="bulk-preview-modal">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-navy">Aperçu</h3>
                <p className="text-xs text-neutral-500">Sujet&nbsp;: <strong>{preview.subject}</strong></p>
                <p className="text-[10px] text-neutral-400">Données simulées : {preview.vars.first_name} {preview.vars.last_name} ({preview.vars.company_name})</p>
              </div>
              <button onClick={() => setPreview(null)} className="text-neutral-400"><X className="h-5 w-5" /></button>
            </div>
            <iframe title="preview" srcDoc={preview.html} className="mt-4 h-[60vh] w-full rounded-xl border border-neutral-200" />
          </div>
        </div>
      )}
    </div>
  );
}
